import { makeAutoObservable, runInAction } from "mobx";
import RootStore from "stores/RootStore";
import ServiceStore from "stores/ServiceStore";
import type { QCInspection, QCConcreteTest, QCCylinderBreak, QCDefect, QCCustomBOM } from "types/qc.types";

/**
 * Follow-up item interface for manager tracking
 */
interface FollowUpItem {
  id: string;
  inspectionId: string;
  productName: string;
  followUpReason: string;
  followUpNotes: string;
  createdAt: string;
  dueDate?: string;
  assignedTo?: string;
  status: "pending" | "in_progress" | "completed";
  completedAt?: string;
  completedNotes?: string;
}

/**
 * QC Manager Dashboard Store
 * Manages the manager's view including approval queue, concrete tests, cylinder breaks,
 * defects, custom BOMs, and follow-up items
 */
class QCManagerDashboardStore {
  rootStore: RootStore;

  // UI State
  activeTab = 0;
  loading = false;
  error: string | null = null;
  success: string | null = null;

  // Approval selection (UUIDs are strings)
  selectedApprovalIds: string[] = [];

  // Manager tabs
  tabs = [
    { label: "Pending Approvals", value: "approvals" },
    { label: "All Inspections", value: "inspections" },
    { label: "Concrete Tests", value: "concrete-tests" },
    { label: "Cylinder Breaks", value: "cylinder-breaks" },
    { label: "Defects", value: "defects" },
    { label: "Custom BOMs", value: "bom" },
    { label: "Follow-ups", value: "follow-ups" },
    { label: "Compliance", value: "compliance" },
  ];

  // Mock data for demonstration
  mockInspections: QCInspection[] = [
    {
      id: "insp-001",
      productId: "prod-001",
      productName: "Box Culvert 4x4x8",
      companyId: "company-001",
      department: "back-deck",
      stage: "pre_pour",
      formNumber: "BC-2024-001",
      jobNumber: "J-2025-001",
      castDate: "2025-12-23",
      overallStatus: "pending",
      inspectorId: "user-002",
      inspectedAt: new Date().toISOString(),
      status: "pending_approval",
      hasDefects: false,
      requiresRework: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      inspector: { id: "user-002", firstName: "Mike", lastName: "Johnson", email: "mike@example.com" },
    },
    {
      id: "insp-002",
      productId: "prod-002",
      productName: "Manhole MH-48",
      companyId: "company-001",
      department: "small-wet",
      stage: "post_pour",
      formNumber: "MH-2024-015",
      jobNumber: "J-2025-002",
      castDate: "2025-12-22",
      overallStatus: "fail",
      inspectorId: "user-003",
      inspectedAt: new Date(Date.now() - 3600000).toISOString(),
      status: "pending_approval",
      hasDefects: true,
      requiresRework: true,
      createdAt: new Date(Date.now() - 3600000).toISOString(),
      updatedAt: new Date(Date.now() - 3600000).toISOString(),
      inspector: { id: "user-003", firstName: "Sarah", lastName: "Williams", email: "sarah@example.com" },
    },
    {
      id: "insp-003",
      productId: "prod-003",
      productName: "3-Sided Culvert 6x8x12",
      companyId: "company-001",
      department: "back-deck",
      stage: "pre_pour",
      formNumber: "3SC-2024-008",
      jobNumber: "J-2025-003",
      castDate: "2025-12-23",
      overallStatus: "pass",
      inspectorId: "user-002",
      inspectedAt: new Date(Date.now() - 7200000).toISOString(),
      status: "pending_approval",
      hasDefects: false,
      requiresRework: false,
      createdAt: new Date(Date.now() - 7200000).toISOString(),
      updatedAt: new Date(Date.now() - 7200000).toISOString(),
      inspector: { id: "user-002", firstName: "Mike", lastName: "Johnson", email: "mike@example.com" },
    },
    {
      id: "insp-004",
      productId: "prod-004",
      productName: "Noise Wall Type 9",
      companyId: "company-001",
      department: "dry-pass",
      stage: "post_pour",
      formNumber: "NW-2024-022",
      jobNumber: "I-405-NW",
      castDate: "2025-12-21",
      overallStatus: "needs_review",
      inspectorId: "user-004",
      inspectedAt: new Date(Date.now() - 86400000).toISOString(),
      status: "pending_approval",
      hasDefects: true,
      requiresRework: false,
      createdAt: new Date(Date.now() - 86400000).toISOString(),
      updatedAt: new Date(Date.now() - 86400000).toISOString(),
      inspector: { id: "user-004", firstName: "Tom", lastName: "Davis", email: "tom@example.com" },
    },
    {
      id: "insp-005",
      productId: "prod-005",
      productName: "Wing Wall 8ft",
      companyId: "company-001",
      department: "back-deck",
      stage: "pre_pour",
      formNumber: "WW-2024-005",
      jobNumber: "J-2025-004",
      castDate: "2025-12-23",
      overallStatus: "pass",
      inspectorId: "user-003",
      inspectedAt: new Date(Date.now() - 1800000).toISOString(),
      status: "pending_approval",
      hasDefects: false,
      requiresRework: false,
      createdAt: new Date(Date.now() - 1800000).toISOString(),
      updatedAt: new Date(Date.now() - 1800000).toISOString(),
      inspector: { id: "user-003", firstName: "Sarah", lastName: "Williams", email: "sarah@example.com" },
    },
    {
      id: "insp-006",
      productId: "prod-006",
      productName: "Pipe 36in RCP",
      companyId: "company-001",
      department: "small-wet",
      stage: "post_pour",
      formNumber: "P-2024-042",
      jobNumber: "J-2025-005",
      castDate: "2025-12-20",
      overallStatus: "pass",
      inspectorId: "user-002",
      inspectedAt: new Date(Date.now() - 172800000).toISOString(),
      status: "approved",
      approvedBy: "user-001",
      approvedAt: new Date(Date.now() - 86400000).toISOString(),
      hasDefects: false,
      requiresRework: false,
      createdAt: new Date(Date.now() - 172800000).toISOString(),
      updatedAt: new Date(Date.now() - 86400000).toISOString(),
      inspector: { id: "user-002", firstName: "Mike", lastName: "Johnson", email: "mike@example.com" },
    },
  ];

  mockConcreteTests: QCConcreteTest[] = [
    {
      id: "ct-001",
      companyId: "company-001",
      batchId: "B-251223-01",
      mixDesign: "Mix #8 (7000 PSI)",
      mixPsi: 7000,
      testDate: new Date().toISOString().split("T")[0],
      testTime: "07:30",
      slump: 5.5,
      airContent: 5.2,
      ambientTemp: 45,
      concreteTemp: 62,
      shopTemp: 55,
      testedBy: "user-001",
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
    {
      id: "ct-002",
      companyId: "company-001",
      batchId: "B-251222-02",
      mixDesign: "Mix #5 (5000 PSI)",
      mixPsi: 5000,
      testDate: new Date(Date.now() - 86400000).toISOString().split("T")[0],
      testTime: "08:15",
      slump: 4.0,
      airContent: 4.8,
      ambientTemp: 48,
      concreteTemp: 64,
      shopTemp: 58,
      testedBy: "user-001",
      createdAt: new Date(Date.now() - 86400000).toISOString(),
      updatedAt: new Date(Date.now() - 86400000).toISOString(),
    },
    {
      id: "ct-003",
      companyId: "company-001",
      batchId: "B-251221-01",
      mixDesign: "SCC Mix #3",
      mixPsi: 6000,
      testDate: new Date(Date.now() - 172800000).toISOString().split("T")[0],
      testTime: "06:45",
      slumpFlow: 24,
      jRing: 22,
      t50FlowRate: 2.5,
      vsiValue: 1,
      airContent: 5.0,
      ambientTemp: 42,
      concreteTemp: 60,
      shopTemp: 52,
      testedBy: "user-001",
      createdAt: new Date(Date.now() - 172800000).toISOString(),
      updatedAt: new Date(Date.now() - 172800000).toISOString(),
    },
  ];

  mockCylinderBreaks: QCCylinderBreak[] = [
    {
      id: "cb-001",
      concreteTestId: "ct-001",
      sampleId: "CYL-251223-01-A",
      breakDate: new Date().toISOString().split("T")[0],
      age: 1,
      diameter: 4,
      length: 8,
      weight: 3.85,
      maxLoad: 0,
      psiResult: 0,
      testedBy: "user-001",
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
    {
      id: "cb-002",
      concreteTestId: "ct-002",
      sampleId: "CYL-251222-02-A",
      breakDate: new Date().toISOString().split("T")[0],
      age: 1,
      diameter: 4,
      length: 8,
      weight: 3.82,
      maxLoad: 0,
      psiResult: 0,
      testedBy: "user-001",
      createdAt: new Date(Date.now() - 86400000).toISOString(),
      updatedAt: new Date(Date.now() - 86400000).toISOString(),
    },
    {
      id: "cb-003",
      concreteTestId: "ct-003",
      sampleId: "CYL-251221-01-B",
      breakDate: new Date(Date.now() + 172800000).toISOString().split("T")[0],
      age: 3,
      diameter: 4,
      length: 8,
      weight: 3.88,
      maxLoad: 0,
      psiResult: 0,
      testedBy: "user-001",
      createdAt: new Date(Date.now() - 172800000).toISOString(),
      updatedAt: new Date(Date.now() - 172800000).toISOString(),
    },
    {
      id: "cb-004",
      concreteTestId: "ct-002",
      sampleId: "CYL-251215-01-C",
      breakDate: new Date(Date.now() - 604800000).toISOString().split("T")[0],
      age: 7,
      diameter: 4,
      length: 8,
      weight: 3.80,
      maxLoad: 75800,
      psiResult: 6030,
      fractureType: 2,
      testedBy: "user-001",
      calculatedBy: "user-001",
      createdAt: new Date(Date.now() - 604800000).toISOString(),
      updatedAt: new Date(Date.now() - 604800000).toISOString(),
    },
  ];

  mockDefects: QCDefect[] = [
    {
      id: "def-001",
      inspectionId: "insp-002",
      productId: "prod-002",
      companyId: "company-001",
      defectType: "Honeycombing",
      description: "Moderate honeycombing observed on north face of manhole section, approximately 6 inches in diameter",
      severity: "major",
      location: "North face, 12 inches from top edge",
      photoUrls: ["/photos/def-001-1.jpg", "/photos/def-001-2.jpg"],
      status: "open",
      correctiveAction: "Fill and patch with approved repair mortar",
      materialsNeeded: "Repair mortar, bonding agent",
      reportedBy: "user-003",
      reportedAt: new Date(Date.now() - 3600000).toISOString(),
      createdAt: new Date(Date.now() - 3600000).toISOString(),
      updatedAt: new Date(Date.now() - 3600000).toISOString(),
    },
    {
      id: "def-002",
      inspectionId: "insp-004",
      productId: "prod-004",
      companyId: "company-001",
      defectType: "Surface Crack",
      description: "Hairline crack observed on decorative surface, 8 inches long",
      severity: "minor",
      location: "Decorative face, center panel",
      photoUrls: ["/photos/def-002-1.jpg"],
      status: "in_progress",
      correctiveAction: "Monitor and document, acceptable per spec",
      reportedBy: "user-004",
      reportedAt: new Date(Date.now() - 86400000).toISOString(),
      createdAt: new Date(Date.now() - 86400000).toISOString(),
      updatedAt: new Date(Date.now() - 43200000).toISOString(),
    },
    {
      id: "def-003",
      productId: "prod-007",
      companyId: "company-001",
      defectType: "Exposed Reinforcement",
      description: "Steel cage visible at corner, insufficient cover",
      severity: "critical",
      location: "Southeast corner",
      photoUrls: ["/photos/def-003-1.jpg", "/photos/def-003-2.jpg", "/photos/def-003-3.jpg"],
      status: "open",
      correctiveAction: "Product must be rejected - structural integrity compromised",
      reportedBy: "user-002",
      reportedAt: new Date(Date.now() - 7200000).toISOString(),
      createdAt: new Date(Date.now() - 7200000).toISOString(),
      updatedAt: new Date(Date.now() - 7200000).toISOString(),
    },
    {
      id: "def-004",
      productId: "prod-008",
      companyId: "company-001",
      defectType: "Bug Holes",
      description: "Multiple small bug holes on bottom surface, within acceptable limits",
      severity: "minor",
      location: "Bottom surface",
      photoUrls: [],
      status: "resolved",
      resolution: "Inspected and determined to be within acceptable tolerance per specification",
      resolvedBy: "user-001",
      resolvedAt: new Date(Date.now() - 259200000).toISOString(),
      reportedBy: "user-003",
      reportedAt: new Date(Date.now() - 345600000).toISOString(),
      createdAt: new Date(Date.now() - 345600000).toISOString(),
      updatedAt: new Date(Date.now() - 259200000).toISOString(),
    },
  ];

  mockCustomBOMs: QCCustomBOM[] = [
    {
      id: "bom-001",
      productId: "prod-custom-001",
      companyId: "company-001",
      productName: "Custom Manhole MH-60 with Access Hatch",
      project: "City of Portland - Water Main",
      jobNumber: "COP-2025-001",
      spacers: [
        { sizeType: '2" Wheel Spacer', quantity: 8, notes: "Standard placement" },
        { sizeType: '1.5" Bar Spacer', quantity: 16 },
      ],
      liftingInserts: [
        { sizeType: "Swift Lift 2.5T", quantity: 4, notes: "90 degree spacing" },
      ],
      hatchesCastings: [
        { sizeType: '24" Round Access Hatch', quantity: 1, notes: "Traffic rated" },
        { sizeType: "Pipe Boot 8in", quantity: 2 },
      ],
      miscMaterials: [
        { sizeType: "Rubber Gasket Kit", quantity: 1 },
        { sizeType: "Stainless Hardware Pack", quantity: 1 },
      ],
      submittedBy: "user-002",
      submittedAt: new Date(Date.now() - 7200000).toISOString(),
      routedTo: "user-ryan",
      status: "pending",
      createdAt: new Date(Date.now() - 7200000).toISOString(),
      updatedAt: new Date(Date.now() - 7200000).toISOString(),
    },
    {
      id: "bom-002",
      productId: "prod-custom-002",
      companyId: "company-001",
      productName: "Custom Box Culvert 5x5x10 with Skew",
      project: "ODOT I-405 Extension",
      jobNumber: "ODOT-405-BC-003",
      spacers: [
        { sizeType: '2" Wheel Spacer', quantity: 12 },
        { sizeType: '2.5" Bar Spacer', quantity: 24, notes: "Heavy gauge" },
      ],
      liftingInserts: [
        { sizeType: "Swift Lift 5T", quantity: 4 },
        { sizeType: "Swift Lift 2.5T", quantity: 2, notes: "Additional for handling" },
      ],
      hatchesCastings: [],
      miscMaterials: [
        { sizeType: "Chamfer Strip 1in", quantity: 40, notes: "Linear feet" },
        { sizeType: "Form Oil", quantity: 2, notes: "Gallons" },
      ],
      submittedBy: "user-003",
      submittedAt: new Date(Date.now() - 86400000).toISOString(),
      routedTo: "user-ryan",
      status: "pending",
      createdAt: new Date(Date.now() - 86400000).toISOString(),
      updatedAt: new Date(Date.now() - 86400000).toISOString(),
    },
    {
      id: "bom-003",
      productId: "prod-custom-003",
      companyId: "company-001",
      productName: "Wing Wall Set for BC-003",
      project: "ODOT I-405 Extension",
      jobNumber: "ODOT-405-WW-003",
      spacers: [
        { sizeType: '1.5" Wheel Spacer', quantity: 8 },
      ],
      liftingInserts: [
        { sizeType: "Swift Lift 2.5T", quantity: 4 },
      ],
      hatchesCastings: [],
      miscMaterials: [],
      submittedBy: "user-003",
      submittedAt: new Date(Date.now() - 172800000).toISOString(),
      routedTo: "user-ryan",
      status: "reviewed",
      reviewedBy: "user-001",
      reviewedAt: new Date(Date.now() - 86400000).toISOString(),
      reviewNotes: "Approved - materials available in stock",
      createdAt: new Date(Date.now() - 172800000).toISOString(),
      updatedAt: new Date(Date.now() - 86400000).toISOString(),
    },
  ];

  mockFollowUpItems: FollowUpItem[] = [
    {
      id: "fu-001",
      inspectionId: "insp-old-001",
      productName: "Box Culvert 4x4x6",
      followUpReason: "Dimensional variance requires engineer review",
      followUpNotes: "Length measurement was 0.5 inches short of specification. Engineer approval needed before shipping.",
      createdAt: new Date(Date.now() - 259200000).toISOString(),
      dueDate: new Date(Date.now() + 86400000).toISOString().split("T")[0],
      assignedTo: "Cassidy Lewis",
      status: "pending",
    },
    {
      id: "fu-002",
      inspectionId: "insp-old-002",
      productName: "Pipe 48in RCP",
      followUpReason: "Customer requested additional documentation",
      followUpNotes: "Customer needs copy of cylinder break results for project file. Email to project manager.",
      createdAt: new Date(Date.now() - 172800000).toISOString(),
      dueDate: new Date().toISOString().split("T")[0],
      assignedTo: "Cassidy Lewis",
      status: "in_progress",
    },
    {
      id: "fu-003",
      inspectionId: "insp-old-003",
      productName: "Noise Wall Type 11",
      followUpReason: "Minor repair completed - verify quality",
      followUpNotes: "Surface repair was completed yesterday. Need to verify repair meets specifications before release.",
      createdAt: new Date(Date.now() - 432000000).toISOString(),
      status: "completed",
      completedAt: new Date(Date.now() - 86400000).toISOString(),
      completedNotes: "Verified repair meets specifications. Product released to yard.",
    },
  ];

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // ============= Actions =============

  setActiveTab = (tab: number) => {
    this.activeTab = tab;
  };

  setLoading = (loading: boolean) => {
    this.loading = loading;
  };

  setError = (error: string | null) => {
    this.error = error;
  };

  setSuccess = (success: string | null) => {
    this.success = success;
  };

  loadDashboardData = async () => {
    this.loading = true;
    this.error = null;

    try {
      const inspectionsStore = this.rootStore["qc-inspectionsStore"] as ServiceStore;
      const concreteTestsStore = this.rootStore["qc-concrete-testsStore"] as ServiceStore;
      const cylinderBreaksStore = this.rootStore["qc-cylinder-breaksStore"] as ServiceStore;

      // Load all data in parallel
      const promises: Promise<any>[] = [];

      if (inspectionsStore) {
        promises.push(
          inspectionsStore.find({
            query: { $sort: { createdAt: -1 }, $limit: 100 },
          })
        );
      }

      if (concreteTestsStore) {
        promises.push(
          concreteTestsStore.find({
            query: { $sort: { testDate: -1 }, $limit: 50 },
          })
        );
      }

      if (cylinderBreaksStore) {
        promises.push(
          cylinderBreaksStore.find({
            query: { $sort: { breakDate: -1 }, $limit: 50 },
          })
        );
      }

      await Promise.all(promises);

      runInAction(() => {
        this.loading = false;
      });
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || "Failed to load dashboard data";
        this.loading = false;
      });
    }
  };

  // ============= Approval Actions =============

  toggleApprovalSelection = (id: string) => {
    const index = this.selectedApprovalIds.indexOf(id);
    if (index === -1) {
      this.selectedApprovalIds.push(id);
    } else {
      this.selectedApprovalIds.splice(index, 1);
    }
  };

  selectAllApprovals = () => {
    this.selectedApprovalIds = this.pendingApprovals.map((a) => a.id);
  };

  clearApprovalSelection = () => {
    this.selectedApprovalIds = [];
  };

  approveSelected = async () => {
    if (this.selectedApprovalIds.length === 0) return;

    this.loading = true;
    try {
      const inspectionsStore = this.rootStore["qc-inspectionsStore"] as ServiceStore;
      const user = (this.rootStore as any).authStore?.user;

      if (inspectionsStore) {
        for (const id of this.selectedApprovalIds) {
          await inspectionsStore.patch(id, {
            status: "approved",
            approvedBy: user?.id,
            approvedAt: new Date().toISOString(),
          });
        }
      }

      runInAction(() => {
        this.success = `${this.selectedApprovalIds.length} inspection(s) approved`;
        this.selectedApprovalIds = [];
        this.loading = false;
      });

      // Reload data
      await this.loadDashboardData();
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || "Failed to approve inspections";
        this.loading = false;
      });
    }
  };

  rejectInspection = async (id: string, reason: string) => {
    this.loading = true;
    try {
      const inspectionsStore = this.rootStore["qc-inspectionsStore"] as ServiceStore;
      const user = (this.rootStore as any).authStore?.user;

      if (inspectionsStore) {
        await inspectionsStore.patch(id, {
          status: "rejected",
          approvedBy: user?.id,
          approvedAt: new Date().toISOString(),
          rejectionReason: reason,
        });
      }

      runInAction(() => {
        this.success = "Inspection rejected";
        this.loading = false;
      });

      await this.loadDashboardData();
    } catch (error: any) {
      runInAction(() => {
        this.error = error.message || "Failed to reject inspection";
        this.loading = false;
      });
    }
  };

  // ============= Defect Actions =============

  resolveDefect = async (id: string, resolution: string) => {
    // In a real implementation, this would call the API
    console.log("Resolving defect", id, resolution);
    this.success = "Defect status updated";
  };

  // ============= BOM Actions =============

  reviewBOM = async (id: string, notes: string) => {
    // In a real implementation, this would call the API
    console.log("Reviewing BOM", id, notes);
    this.success = "BOM reviewed and sent to Ryan";
  };

  // ============= Follow-up Actions =============

  completeFollowUp = async (id: string, notes: string) => {
    // In a real implementation, this would call the API
    console.log("Completing follow-up", id, notes);
    this.success = "Follow-up item completed";
  };

  // ============= Computed Values =============

  get inspectionsStore(): ServiceStore | null {
    return (this.rootStore["qc-inspectionsStore"] as ServiceStore) || null;
  }

  // Use mock data if no real data available
  get inspections(): QCInspection[] {
    const storeEntities = this.inspectionsStore?.entities || [];
    return storeEntities.length > 0 ? (storeEntities as unknown as QCInspection[]) : this.mockInspections;
  }

  get pendingApprovals(): QCInspection[] {
    return this.inspections.filter((i: QCInspection) => i.status === "pending_approval");
  }

  get pendingCount(): number {
    return this.pendingApprovals.length;
  }

  get approvedInspections(): QCInspection[] {
    return this.inspections.filter((i: QCInspection) => i.status === "approved");
  }

  get failedInspections(): QCInspection[] {
    return this.inspections.filter((i: QCInspection) => i.overallStatus === "fail");
  }

  get concreteTests(): QCConcreteTest[] {
    const store = this.rootStore["qc-concrete-testsStore"] as ServiceStore;
    const storeEntities = store?.entities || [];
    return storeEntities.length > 0 ? (storeEntities as unknown as QCConcreteTest[]) : this.mockConcreteTests;
  }

  get cylinderBreaks(): QCCylinderBreak[] {
    const store = this.rootStore["qc-cylinder-breaksStore"] as ServiceStore;
    const storeEntities = store?.entities || [];
    return storeEntities.length > 0 ? (storeEntities as unknown as QCCylinderBreak[]) : this.mockCylinderBreaks;
  }

  get upcomingBreaks(): QCCylinderBreak[] {
    const today = new Date().toISOString().split("T")[0];
    return this.cylinderBreaks.filter((b) => b.breakDate >= today && b.psiResult === 0);
  }

  get cylinderBreaksDueToday(): QCCylinderBreak[] {
    const today = new Date().toISOString().split("T")[0];
    return this.cylinderBreaks.filter((b) => b.breakDate === today && b.psiResult === 0);
  }

  // Defects
  get defects(): QCDefect[] {
    const store = this.rootStore["qc-defectsStore"] as ServiceStore;
    const storeEntities = store?.entities || [];
    return storeEntities.length > 0 ? (storeEntities as unknown as QCDefect[]) : this.mockDefects;
  }

  get openDefects(): QCDefect[] {
    return this.defects.filter((d) => d.status === "open" || d.status === "in_progress");
  }

  get criticalDefects(): QCDefect[] {
    return this.defects.filter((d) => d.severity === "critical" && d.status === "open");
  }

  // Custom BOMs
  get customBOMs(): QCCustomBOM[] {
    const store = this.rootStore["qc-custom-bomsStore"] as ServiceStore;
    const storeEntities = store?.entities || [];
    return storeEntities.length > 0 ? (storeEntities as unknown as QCCustomBOM[]) : this.mockCustomBOMs;
  }

  get pendingBOMs(): QCCustomBOM[] {
    return this.customBOMs.filter((b) => b.status === "pending");
  }

  // Follow-up items
  get followUpItems(): FollowUpItem[] {
    return this.mockFollowUpItems;
  }

  get pendingFollowUps(): FollowUpItem[] {
    return this.followUpItems.filter((f) => f.status !== "completed");
  }

  // Inspections requiring follow-up (based on approval flags)
  get inspectionsWithFollowUp(): QCInspection[] {
    return this.inspections.filter((i) => i.requiresRework || i.hasDefects);
  }

  // ============= Dashboard Summary Stats =============

  get totalPendingItems(): number {
    return (
      this.pendingApprovals.length +
      this.cylinderBreaksDueToday.length +
      this.criticalDefects.length +
      this.pendingBOMs.length +
      this.pendingFollowUps.length
    );
  }

  get urgentItemsCount(): number {
    return this.cylinderBreaksDueToday.length + this.criticalDefects.length;
  }

  // ============= Reset =============

  clearMessages = () => {
    this.error = null;
    this.success = null;
  };

  reset = () => {
    this.activeTab = 0;
    this.loading = false;
    this.error = null;
    this.success = null;
    this.selectedApprovalIds = [];
  };
}

export default QCManagerDashboardStore;
